"use client";
import React, { useEffect, useState } from "react";
import { BondData } from "@/app/interface/bonds";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ButtonSize, ButtonVariant, RegularButton } from "@/stories/button/button";
import BondProductCard from "@/app/listed/components/bond-product-card";
import BondsDisclaimer from "@/app/listed/components/bonds-disclaimer";
import { isVisibleOnBrowse } from "@/app/utils/product-visibility";

interface BondsListingProps {
    bonds?: BondData[];
}

const BondsListing: React.FC<BondsListingProps> = ({ bonds }) => {
    const [activeTab, setActiveTab] = useState<string>("live");
    const [filteredProducts, setFilteredProducts] = useState<BondData[]>([]);
    const [showAll, setShowAll] = useState<boolean>(false);
    const initialDisplayCount = 6;

    useEffect(() => {
        if (!bonds) return;
        setShowAll(false); // Reset show all when tab changes
    
        const tmpFilteredData = bonds.filter(
          (bond) => 
          bond.isActive &&
          isVisibleOnBrowse(bond) &&
          bond.category === activeTab
        )
        
        const tempSortedCards = [...tmpFilteredData].sort((a, b) => {
            const orderA = a?.order ?? Infinity; // Assign a high value for null
            const orderB = b?.order ?? Infinity; // Assign a high value for null
            return orderA - orderB;
        });

        setFilteredProducts(tempSortedCards);
    },[bonds, activeTab]);

    const displayedProducts = showAll ? filteredProducts : filteredProducts.slice(0, initialDisplayCount);

    const handleToggleShow = () => {
        setShowAll(!showAll);
    };

    const showButton = filteredProducts.length > initialDisplayCount;

    return (
        <div className="mx-auto flex w-full flex-col md:gap-[54px] pt-6" id="bonds-listing-container">
            <Tabs
                defaultValue={activeTab}
                id="bonds-category-navigation"
                className="w-full border-b border-b-neutralOpacityDark-10 md:mx-auto md:!w-[1224px]"
                onValueChange={(value) => {
                    setActiveTab(value);
                }}
            >
                <TabsList className="flex w-full justify-start gap-6 bg-transparent !px-0" id="bonds-category-navigation-list">
                    <TabsTrigger className="!w-[114px] py-s7 text-sm font-medium leading-[22.4px] tracking-[0.14px] text-neutral-300 inline-flex items-center justify-center whitespace-nowrap h-11 md:w-[236px] transition-all disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-b-brand-primary-500 data-[state=active]:text-brand-primary-500" value="live" id="bonds-category-navigation-live">
                        Live
                    </TabsTrigger>
                    <TabsTrigger className="!w-[114px] py-s7 text-sm font-medium leading-[22.4px] tracking-[0.14px] text-neutral-300 inline-flex items-center justify-center whitespace-nowrap h-11 md:w-[236px] transition-all disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-b-brand-primary-500 data-[state=active]:text-brand-primary-500" value="upcoming" id="bonds-category-navigation-upcoming">
                        Upcoming
                    </TabsTrigger>
                    <TabsTrigger className="!w-[114px] py-s7 text-sm font-medium leading-[22.4px] tracking-[0.14px] text-neutral-300 inline-flex items-center justify-center whitespace-nowrap h-11 md:w-[236px] transition-all disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-b-brand-primary-500 data-[state=active]:text-brand-primary-500" value="historical" id="bonds-category-navigation-historical">
                        Historical
                    </TabsTrigger>
                </TabsList>
            </Tabs>

            <div 
                id="bonds-listing-grid"
                className={`grid grid-cols-1 gap-9 md:grid-cols-3 !w-full transition-[max-height] duration-500 ease-in-out md:!w-[1224px] mx-auto md:p-0 p-6
                    ${showAll 
                        ? `max-h-[3000px] md:max-h-[2000px] overflow-auto [&::-webkit-scrollbar]:w-1
                        [&::-webkit-scrollbar-track]:!bg-transparent
                        [&::-webkit-scrollbar-track]:rounded-full
                        [&::-webkit-scrollbar-thumb]:!bg-transparent
                        dark:[&::-webkit-scrollbar-track]:!bg-transparent
                        dark:[&::-webkit-scrollbar-thumb]:!bg-transparent
                        [&::-webkit-scrollbar-thumb]:h-[50px]` // Adjust thumb height here
                        : "max-h-[1885px] md:max-h-[972px] overflow-hidden"} 
                `}
            >
                {displayedProducts?.map((product, index) => 
                    (
                        <div key={index} id="bonds-product-card-main">
                            <BondProductCard product={product} />
                        </div>
                    )
                )}
            </div>

            {showButton && (
                <div className="w-full flex justify-center py-6" id="bonds-show-more">
                    <RegularButton
                        size={ButtonSize.Large}
                        showRightIcon={false}
                        noIcon
                        variant={ButtonVariant.SecondaryDefault} 
                        onClick={handleToggleShow}
                        className="min-w-[180px] rounded-full h-[49px]"
                        label={showAll ? "Show Less" : "Show More"}
                    />
                </div>
            )}

            <BondsDisclaimer />
        </div>
    );
};

export default BondsListing;
